import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { ROUTES, saveReturnTo } from '@/config/routes';

interface UseAuthGuardOptions {
  requireAdmin?: boolean;
}

interface UseAuthGuardResult {
  isLoading: boolean;
  isBlocked: boolean;
}

/**
 * Custom hook for common auth guard logic
 * Handles authentication checks, admin role validation, and navigation
 * Returns loading and blocked states to determine what the component should render
 */
export const useAuthGuard = (options: UseAuthGuardOptions = {}): UseAuthGuardResult => {
  const { requireAdmin = true } = options;
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isAdmin } = useAdminAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;

    // Set ref first in Strict Mode to prevent double navigation
    if (hasNavigatedRef.current) return;

    if (!isAuthenticated) {
      hasNavigatedRef.current = true;
      saveReturnTo(location.pathname);
      navigate(ROUTES.LOGIN);
      return;
    }

    if (requireAdmin && !isAdmin) {
      hasNavigatedRef.current = true;
      navigate(ROUTES.HOME);
      return;
    }

    return () => {
      hasNavigatedRef.current = false;
    };
  }, [isAuthenticated, isAdmin, authLoading, requireAdmin, navigate, location.pathname]);

  return {
    isLoading: authLoading,
    isBlocked: !isAuthenticated || (requireAdmin && !isAdmin),
  };
};